// Feature L — the suggestion soft lock, as pure functions over timestamps so
// the acquire/extend/expire/takeover rules can be unit-tested directly and are
// identical on both sides. The lock_token is cooperative (a random per-browser
// id), not a security secret — the point is to stop two teammates clobbering
// each other, not to defend against a determined user.

export const LOCK_TTL_MS = 90_000;

export interface Lock {
  lockToken: string;
  lockExpiresAt: number;
  lockHolderName: string;
}

/** True while a lock is fresh (not yet expired). Empty token = no lock. */
export function isLockActive(
  lock: { lockToken: string; lockExpiresAt: number },
  now: number,
): boolean {
  return lock.lockToken !== '' && lock.lockExpiresAt > now;
}

/** Do I currently hold a fresh lock? */
export function iHoldLock(
  lock: { lockToken: string; lockExpiresAt: number },
  myToken: string,
  now: number,
): boolean {
  return myToken !== '' && lock.lockToken === myToken && lock.lockExpiresAt > now;
}

/** Is a fresh lock held by someone else? (drives the read-only "X is editing"
 *  state on the other visitors' screens). */
export function lockedByOther(
  lock: { lockToken: string; lockExpiresAt: number },
  myToken: string,
  now: number,
): boolean {
  return lock.lockToken !== '' && lock.lockToken !== myToken && lock.lockExpiresAt > now;
}

/** Can `myToken` acquire/extend the lock right now? Mirrors the server's atomic
 *  UPDATE `... AND (lock_token = ? OR lock_expires_at < ?)` so the client can
 *  predict the outcome. True when the lock is mine, empty, or stale — that
 *  stale case is exactly "takeover". */
export function canAcquire(
  lock: { lockToken: string; lockExpiresAt: number },
  myToken: string,
  now: number,
): boolean {
  return lock.lockToken === myToken || lock.lockToken === '' || lock.lockExpiresAt <= now;
}

/** Derive reviewer UI state without receiving any browser's lock token. */
export function publicLockState(
  lock: { lock_present: boolean; lock_owned: boolean; lock_expires_at: number },
  now: number,
): { iHold: boolean; byOther: boolean; staleForeign: boolean } {
  return {
    iHold: lock.lock_present && lock.lock_owned && lock.lock_expires_at > now,
    byOther: lock.lock_present && !lock.lock_owned && lock.lock_expires_at > now,
    staleForeign: lock.lock_present && !lock.lock_owned && lock.lock_expires_at <= now,
  };
}
